import { fuseauValide, momentDansUnFuseau } from '@nextilt/commun';
import type { Bulle, DateAuPlusTot } from './api/types';

/**
 * **Les dates en mots, à un seul endroit** (chantier nettoyage-mots, ticket 01) : un jour, une
 * heure, une durée ou un instant dit comme on le lit, et « aujourd'hui », le jour de
 * l'utilisateur. Sans rendu. Rien n'y est calculé sur le moteur : les jours, les heures et leur
 * ordre viennent du serveur ; on les met en mots, et l'on ne compte que des jours de calendrier.
 *
 * Les phrases qui portent une date (« Avant le 15 octobre », « Au plus tôt : … ») restent dans
 * les fichiers de mots (`mots/`, renvoyés par `vocabulaire.ts`) et appellent ce fichier ; chaque format `Intl` n'existe qu'ici, une fois.
 */

/* ── Les formats, chacun écrit une fois ── */

const FORMATS = {
  quantieme: { day: 'numeric' },
  jourEtMois: { day: 'numeric', month: 'long' },
  jourMoisEtAnnee: { day: 'numeric', month: 'long', year: 'numeric' },
  jourEtMoisAbrege: { day: 'numeric', month: 'short' },
  jourEtMoisEnChiffres: { day: '2-digit', month: '2-digit' },
  semaineJourEtMois: { weekday: 'long', day: 'numeric', month: 'long' },
  semaineJourEtMoisAbreges: { weekday: 'short', day: 'numeric', month: 'short' },
  semaineAbregeeEtQuantieme: { weekday: 'short', day: 'numeric' },
  semaine: { weekday: 'long' },
  semaineAbregee: { weekday: 'short' },
  mois: { month: 'long' },
  moisAbrege: { month: 'short' },
  moisEtAnnee: { month: 'long', year: 'numeric' },
  heure: { hour: '2-digit', minute: '2-digit' },
  semaineQuantiemeEtHeure: { weekday: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
  semaineJourMoisEtHeure: { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

type NomDuFormat = keyof typeof FORMATS;
const formats = new Map<NomDuFormat, Intl.DateTimeFormat>();

/** Une date mise en forme, en français, à l'heure de la machine. */
function enForme(nom: NomDuFormat, date: Date): string {
  let format = formats.get(nom);
  if (!format) {
    format = new Intl.DateTimeFormat('fr-FR', FORMATS[nom]);
    formats.set(nom, format);
  }
  return format.format(date);
}

/* ── Un jour « aaaa-mm-jj » ── */

/** Lit un jour « aaaa-mm-jj » envoyé par le serveur, pour le seul besoin de l'affichage. */
export function jourLocal(jour: string): Date | null {
  const [an, mois, quantieme] = jour.split('-').map(Number);
  return an && mois && quantieme ? new Date(an, mois - 1, quantieme) : null;
}

/** « 3 octobre », « jeudi 1 octobre » : sans jamais fabriquer d'instant. */
export function enFrancais(jour: string, avecJourDeSemaine = false): string {
  const d = jourLocal(jour);
  return d ? enForme(avecJourDeSemaine ? 'semaineJourEtMois' : 'jourEtMois', d) : jour;
}

/** « 31 octobre 2026 ». */
export function enFrancaisAvecAnnee(jour: string): string {
  const d = jourLocal(jour);
  return d ? enForme('jourMoisEtAnnee', d) : jour;
}

/** « mar. 15 » : le jour de la semaine et le quantième. */
export function jourCourt(jour: string): string {
  const d = jourLocal(jour);
  return d ? enForme('semaineAbregeeEtQuantieme', d) : jour;
}

/** Le quantième, « 1er » pour le premier du mois. */
export function quantieme(jour: string): string {
  const q = Number(jour.split('-')[2]);
  return q === 1 ? '1er' : String(q);
}

/** « lundi 5 octobre », « jeudi 1er octobre ». */
export function jourEnToutesLettres(jour: string): string {
  const d = jourLocal(jour);
  if (!d) return jour;
  return `${enForme('semaine', d)} ${quantieme(jour)} ${enForme('mois', d)}`;
}

/** « Octobre 2026 ». */
export function moisEnLettres(mois: string): string {
  const d = jourLocal(`${mois}-01`);
  if (!d) return mois;
  const texte = enForme('moisEtAnnee', d);
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

/** « 1er oct. » pour la première case d'un mois, le quantième seul sinon. */
export function numeroDeCase(jour: string): string {
  const d = jourLocal(jour);
  if (!d || d.getDate() !== 1) return String(Number(jour.split('-')[2]));
  return `1er ${enForme('moisAbrege', d)}`;
}

/** « 28 sept. », « 1er oct. » : le quantième de l'Agenda et le mois abrégé (Agenda au téléphone). */
export function quantiemeEtMoisAbrege(jour: string): string {
  const d = jourLocal(jour);
  return d ? `${quantieme(jour)} ${enForme('moisAbrege', d)}` : jour;
}

/** « jeu » : le nom court du jour, sans son point (Agenda au téléphone). */
export function nomCourtDuJour(jour: string): string {
  const d = jourLocal(jour);
  return d ? enForme('semaineAbregee', d).replace('.', '') : '';
}

/** « lun. 27 oct. » : le jour de la semaine, le quantième et le mois abrégés (le panneau d'analyse). */
export function jourAbrege(jour: string): string {
  const d = jourLocal(jour);
  return d ? enForme('semaineJourEtMoisAbreges', d) : jour;
}

/** « 30/10 », « 01/12 » : le jour et le mois en chiffres (l'arbre d'un projet au téléphone). */
export function jourEnChiffres(jour: string): string {
  const d = jourLocal(jour);
  return d ? enForme('jourEtMoisEnChiffres', d) : jour;
}

/** « 26 oct. », « 15 mars » : le jour d'une date sans heure, le mois abrégé (fiche du téléphone). */
export function jourEtMoisAbrege(jour: string): string {
  const d = jourLocal(jour);
  return d ? enForme('jourEtMoisAbrege', d) : jour;
}

/**
 * « 6 – 12 avril », « 27 avril – 3 mai » : une semaine est une plage (la Chronologie). Le mois
 * n'est répété à gauche que lorsque la semaine en enjambe deux.
 */
export function plageDeLaSemaine(debut: string, fin: string): string {
  const lundi = jourLocal(debut);
  const dimanche = jourLocal(fin);
  if (!lundi || !dimanche) return `${debut} – ${fin}`;
  // Même mois : on compare le texte « aaaa-mm », sans aucun calcul de date.
  const memeMois = debut.slice(0, 7) === fin.slice(0, 7);
  return `${enForme(memeMois ? 'quantieme' : 'jourEtMois', lundi)} – ${enForme('jourEtMois', dimanche)}`;
}

/* ── Les heures, les durées, les instants ── */

/** « 9 h », « 9 h 30 » : l'heure ronde ne traîne pas ses deux zéros. */
export function heureLisible(heure: string): string {
  const [h, m] = heure.split(':');
  return m === '00' ? `${Number(h)} h` : `${Number(h)} h ${m}`;
}

/** « 14 h 30 » d'un instant, à l'heure locale. */
export function heureDe(instant: Date): string {
  return heureLisible(enForme('heure', instant));
}

/** « 14:00 » d'un instant, à l'heure locale (la fiche du téléphone). */
export function heureAuxChiffres(instant: Date): string {
  return enForme('heure', instant);
}

/** « 45 min », « 1 h », « 1 h 30 » : la durée en mots, écrite une fois dans `@nextilt/commun` (chantier duree-a-la-minute). */
export { dureeEnMots as dureeLisible } from '@nextilt/commun';

/** Le délai dans son unité : « 10 jours », « 1 jour », « 2 mois ». Zéro se dit. */
export function delaiEnMots(bulle: Pick<Bulle, 'delaiJours' | 'delaiMois'>): string | null {
  if (bulle.delaiMois != null) return `${bulle.delaiMois} mois`;
  if (bulle.delaiJours != null) return `${bulle.delaiJours} ${bulle.delaiJours > 1 ? 'jours' : 'jour'}`;
  return null;
}

/** « mer. 4 mars » : assemblé pièce par pièce, pour ne pas dépendre de la ponctuation d'Intl. */
export function jourAbregeDUnInstant(instant: Date): string {
  return `${enForme('semaineAbregee', instant)} ${enForme('jourEtMoisAbrege', instant)}`;
}

/** « mercredi 4 mars ». */
export function jourLongDUnInstant(instant: Date): string {
  return enForme('semaineJourEtMois', instant);
}

/** « mercredi 4 14:00 » : le jour et l'heure d'un rendez-vous (Maintenant, Projets). */
export function jourEtHeureDUnInstant(instant: Date): string {
  return enForme('semaineQuantiemeEtHeure', instant);
}

/** Un instant ISO en toutes lettres. Une *date* n'est pas un *instant* : les deux existent. */
export function instantEnToutesLettres(iso: string): string {
  return enForme('semaineJourMoisEtHeure', new Date(iso));
}

/**
 * Le jour d'un instant ISO, « 4 mars », et l'année avec, « 4 mars 2025 », quand ce n'est pas
 * celle de `maintenant` (la création d'une clé d'accès, chantier parametres-du-compte, 03).
 */
export function jourDUnInstant(iso: string, maintenant = new Date()): string {
  const instant = new Date(iso);
  return enForme(instant.getFullYear() === maintenant.getFullYear() ? 'jourEtMois' : 'jourMoisEtAnnee', instant);
}

/**
 * Depuis quand un instant passé, pour la dernière activité d'une session ou la dernière
 * utilisation d'une clé (chantier parametres-du-compte, 03) : dans l'heure (le serveur ne la
 * note qu'une fois par heure, et une horloge un peu en avance compte aussi), les heures pleines
 * dans la journée passée, puis le jour. La phrase est dans `mots/compte.ts`.
 */
export type DepuisQuand = { sorte: 'dans_l_heure' } | { sorte: 'heures'; heures: number } | { sorte: 'jour'; jour: string };

export function depuisQuand(iso: string, maintenant = new Date()): DepuisQuand {
  const heures = Math.floor((maintenant.getTime() - new Date(iso).getTime()) / 3_600_000);
  if (heures < 1) return { sorte: 'dans_l_heure' };
  if (heures < 24) return { sorte: 'heures', heures };
  return { sorte: 'jour', jour: jourDUnInstant(iso, maintenant) };
}

/** Un instant ISO vers ce que `datetime-local` sait lire, en heure locale. */
export function instantPourLeChamp(iso: string | null): string {
  if (!iso) return '';
  const instant = new Date(iso);
  const decale = new Date(instant.getTime() - instant.getTimezoneOffset() * 60_000);
  return decale.toISOString().slice(0, 16);
}

/* ── Les jours qu'on compte, et aujourd'hui ── */

/** Lit « aaaa-mm-jj » en jour UTC : compter des jours ne bute jamais sur un changement d'heure. */
function enJourUTC(jour: string): Date {
  const [an, mois, quantieme] = jour.split('-').map(Number);
  return new Date(Date.UTC(an ?? 1970, (mois ?? 1) - 1, quantieme ?? 1));
}

/** Le jour `n` jours plus loin (ou plus tôt si `n` est négatif). */
export function decaler(jour: string, n: number): string {
  const d = enJourUTC(jour);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Combien de jours pleins séparent deux jours « aaaa-mm-jj » (négatif si `a` est avant `de`). */
export function joursEntre(de: string, a: string): number {
  return Math.round((enJourUTC(a).getTime() - enJourUTC(de).getTime()) / 86_400_000);
}

/**
 * Le fuseau du navigateur (« Europe/Paris »), celui que l'app envoie au serveur en `x-fuseau`
 * à chaque requête (`partage/api/client.ts`) ; `null` s'il ne se lit pas.
 */
export function fuseauDuNavigateur(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * **Aujourd'hui**, en « aaaa-mm-jj » : le jour de l'utilisateur, dans le fuseau envoyé au
 * serveur, la même règle que lui (`momentDansUnFuseau`), jamais le jour en temps universel.
 * Sans fuseau connu, le calendrier de la machine. Le seul « aujourd'hui » de l'app : un écran
 * qui a celui du serveur (`aujourdhui` d'une réponse) le préfère.
 */
export function aujourdhui(maintenant = new Date(), fuseau: string | null = fuseauDuNavigateur()): string {
  if (fuseauValide(fuseau)) return momentDansUnFuseau(maintenant, fuseau).jour;
  const deux = (n: number) => String(n).padStart(2, '0');
  return `${maintenant.getFullYear()}-${deux(maintenant.getMonth() + 1)}-${deux(maintenant.getDate())}`;
}

/** Aujourd'hui en lettres, « jeudi 8 octobre » ou « jeu. 8 oct. », pour la tête de l'écran. */
export function aujourdhuiEnLettres(court = false, maintenant = new Date()): string {
  const d = jourLocal(aujourdhui(maintenant)) ?? maintenant;
  return enForme(court ? 'semaineJourEtMoisAbreges' : 'semaineJourEtMois', d);
}

/* ── La date au plus tôt ── */

/**
 * Le seul choix entre les trois réponses de la date au plus tôt : une date, « pas de date »
 * (personne ne l'attend), « pas encore calculable ». Les mots de chaque écran sont dans
 * `vocabulaire.ts`.
 */
export function selonLaDateAuPlusTot(
  date: DateAuPlusTot,
  mots: { date: (jour: string) => string; quandTuVeux: string; pasCalculable: string }
): string {
  switch (date.sorte) {
    case 'date':
      return mots.date(date.jour);
    case 'quand_tu_veux':
      return mots.quandTuVeux;
    default:
      return mots.pasCalculable;
  }
}
